import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Page, Storybook } from '../../types/storybook';

export interface EditorPageDomain {
  currentPageIndex: number;
  currentPage?: Page;
  setCurrentPageIndex: (index: number) => void;
  replaceCurrentPage: (page: Page) => void;
}

export const clampPageIndex = (index: number, pageCount: number): number => {
  if (pageCount <= 0 || !Number.isFinite(index)) return 0;
  return Math.min(pageCount - 1, Math.max(0, Math.trunc(index)));
};

export const replacePageAtIndex = (
  storybook: Storybook,
  index: number,
  page: Page
): Storybook => {
  if (storybook.pages.length === 0) return storybook;
  const resolvedIndex = clampPageIndex(index, storybook.pages.length);
  if (storybook.pages[resolvedIndex] === page) return storybook;
  const pages = [...storybook.pages];
  pages[resolvedIndex] = page;
  return { ...storybook, pages };
};

export const renumberContentPages = (pages: Page[]): Page[] => {
  let contentPageNumber = 1;
  return pages.map(page => {
    if (page.isSystemPage) return page;
    const numberedPage = { ...page, pageNumber: contentPageNumber };
    contentPageNumber += 1;
    return numberedPage;
  });
};

export const insertPageAfterCurrent = (
  storybook: Storybook,
  currentPageIndex: number,
  newPage: Page
): { storybook: Storybook; insertedIndex: number } => {
  const currentIndex = clampPageIndex(currentPageIndex, storybook.pages.length);
  const backCoverIndex = storybook.pages.findIndex(page => (
    page.role === 'back-cover' || (page.isSystemPage && /back/i.test(page.pageTitle || ''))
  ));
  const insertedIndex = backCoverIndex >= 0
    ? Math.min(currentIndex + 1, backCoverIndex)
    : currentIndex + 1;
  const pages = [
    ...storybook.pages.slice(0, insertedIndex),
    newPage,
    ...storybook.pages.slice(insertedIndex)
  ];
  return {
    insertedIndex,
    storybook: { ...storybook, pages: renumberContentPages(pages) }
  };
};

export const deletePageAtIndex = (
  storybook: Storybook,
  pageIndex: number
): Storybook => {
  if (pageIndex < 0 || pageIndex >= storybook.pages.length) return storybook;
  return {
    ...storybook,
    pages: renumberContentPages(storybook.pages.filter((_, index) => index !== pageIndex))
  };
};

export const reorderBookPages = (
  storybook: Storybook,
  fromIndices: number[],
  toIndex: number,
  currentPageIndex: number
): { storybook: Storybook; selectedIndex: number } => {
  const uniqueIndices = Array.from(new Set(fromIndices))
    .filter(index => index >= 0 && index < storybook.pages.length)
    .sort((left, right) => left - right);
  if (uniqueIndices.length === 0) {
    return { storybook, selectedIndex: clampPageIndex(currentPageIndex, storybook.pages.length) };
  }
  const selectedPageId = storybook.pages[
    clampPageIndex(currentPageIndex, storybook.pages.length)
  ]?.id;
  const movingPages = uniqueIndices.map(index => storybook.pages[index]);
  const pages = storybook.pages.filter((_, index) => !uniqueIndices.includes(index));
  const removedBeforeTarget = uniqueIndices.filter(index => index < toIndex).length;
  const insertionIndex = clampPageIndex(
    toIndex - removedBeforeTarget,
    pages.length + 1
  );
  pages.splice(insertionIndex, 0, ...movingPages);
  const numberedPages = pages.map((page, index) => ({ ...page, pageNumber: index + 1 }));
  return {
    storybook: { ...storybook, pages: numberedPages },
    selectedIndex: selectedPageId
      ? numberedPages.findIndex(page => page.id === selectedPageId)
      : insertionIndex
  };
};

export const useEditorPageDomain = ({
  storybook,
  setStorybook
}: {
  storybook: Storybook | null;
  setStorybook: Dispatch<SetStateAction<Storybook | null>>;
}): EditorPageDomain => {
  const [requestedPageIndex, setRequestedPageIndex] = useState(0);
  const pageCount = storybook?.pages.length || 0;
  const currentPageIndex = clampPageIndex(requestedPageIndex, pageCount);

  useEffect(() => {
    if (currentPageIndex !== requestedPageIndex) {
      setRequestedPageIndex(currentPageIndex);
    }
  }, [currentPageIndex, requestedPageIndex]);

  const setCurrentPageIndex = useCallback((index: number) => {
    // Keep the request until the accompanying page-list update is rendered.
    // This lets "insert then select" navigate to a newly appended page while
    // the exposed index remains clamped against the rendered list.
    setRequestedPageIndex(Number.isFinite(index) ? Math.trunc(index) : 0);
  }, []);

  const replaceCurrentPage = useCallback((page: Page) => {
    setStorybook(current => current
      ? replacePageAtIndex(current, currentPageIndex, page)
      : current);
  }, [currentPageIndex, setStorybook]);

  const currentPage = useMemo(
    () => storybook?.pages[currentPageIndex],
    [currentPageIndex, storybook]
  );

  return {
    currentPageIndex,
    currentPage,
    setCurrentPageIndex,
    replaceCurrentPage
  };
};
